import React from 'react';
import { ReportParams, ReportConfig } from '@/component/common/report/report';
import { AiSummaryModule } from '@/component/common/report/reportConfigModel';
import { postEventStream } from '@/utils/httpChat';
import Markdown from 'react-markdown';
import useQuickBiTemplate from '@/component/Template/hooks/useQuickBiTemplate';
import remarkGfm from 'remark-gfm';
import { RightOutlined, SyncOutlined, LineChartOutlined } from '@ant-design/icons';
import { Button, Drawer } from 'antd';
import { useParams } from 'react-router-dom';
import { getAiReportConfigCached } from '@/component/Report/services/ai/aiReportCache';
import styled from 'styled-components';
import { mergeColumns } from '@/component/Report/CustomTable/utils/utilsTable';

// 类型 Props 处
type Props = {
  reportConfig?: ReportConfig;
  moduleItem: AiSummaryModule;
  params: ReportParams;
  loading?: boolean;
  templateCode?: string; // 新增：支持外部传入模板编码
  selectedReport?: string; // 新增：用于设置 selected_report
  onTriggerAiSearch?: () => void; // 新增：触发父组件执行筛选（AI）
  isAiChat?: boolean; // 新增：与 PageComponent 保持一致的展示逻辑
  isMainFilter?: boolean; // 新增：与 PageComponent 保持一致的展示逻辑
};

// 组件定义：AiSummary
export const AiSummary: React.FC<Props> = ({
  reportConfig,
  moduleItem,
  params,
  loading,
  templateCode,
  selectedReport,
  onTriggerAiSearch,
  isAiChat = false,
  isMainFilter = true,
}) => {
  const [summary, setSummary] = React.useState<string>('');
  const abortRef = React.useRef<AbortController | null>(null);
  const FIXED_MAX_HEIGHT_PX = 150;
  const ANALYZE_DEBOUNCE_MS = 600;
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [drawerVisible, setDrawerVisible] = React.useState<boolean>(false);
  // 新增：重新总结触发与生成中状态
  const [refreshTick, setRefreshTick] = React.useState<number>(0);
  const [isGenerating, setIsGenerating] = React.useState<boolean>(false);
  const handleRegenerate = React.useCallback(() => {
    setRefreshTick(t => t + 1);
  }, []);


  // 新增：从 URL 中获取报表名，并初始化 AI 展示开关
  const { reportName = '' } = useParams<{ reportName: string }>();
  const [showAiButton, setShowAiButton] = React.useState<boolean>(false);

  const filterThinkContent = React.useCallback((text: string) => {
    if (!text) return '';
    let result = text.replace(/<think>[\s\S]*?<\/think>/g, '');
    const openIdx = result.lastIndexOf('<think>');
    if (openIdx !== -1 && result.indexOf('</think>', openIdx) === -1) {
      result = result.slice(0, openIdx);
    }
    return result;
  }, []);

  const sessionId = React.useMemo(() => {
    const cookie = document.cookie || '';
    const found = cookie.split(';').map(s => s.trim()).find(s => s.startsWith('session_id='));
    if (!found) return '';
    return decodeURIComponent(found.split('=')[1] || '');
  }, []);

  React.useEffect(() => {
    const initAiReportData = async () => {
      const { showAiButton } = await getAiReportConfigCached(reportName);
      setShowAiButton(showAiButton);
    };
    if (reportName) {
      initAiReportData();
    }
  }, [reportName]);

  // 使用模板 Hook
  const { template: tpl } = useQuickBiTemplate(
    reportConfig?.config?.slug,
    templateCode
  );

  // 基于 BaseTable 的字段策略：合并本地缓存后的可见字段
  const summaryTemplateMeta = React.useMemo(() => {
    if (tpl && (tpl as any).fields && reportConfig?.config?.slug && reportConfig?.accounts_id) {
      const merged = mergeColumns(tpl as any, {
        report_name: reportConfig.config.slug,
        accounts_id: reportConfig.accounts_id,
        showSeriesNumber: true,
        // 若无默认展示字段配置，mergeColumns 会自行处理为空的情况
        defaultDisplayFields: (moduleItem as any)?.defaultDisplayFields,
      });
      return (merged || [])
        .filter((c: any) => c && c.show === 'T' && !c.isSerial && typeof c.dataIndex === 'string')
        .map((c: any) => ({ code: c.dataIndex as string, title: typeof c.title === 'string' ? c.title : undefined }));
    }
    // 兜底：直接使用模板字段
    return ((tpl as any)?.fields || []).map((f: any) => ({ code: f.code, title: f.title }));
  }, [tpl, reportConfig, moduleItem]);

  // 计算完整表格与预览表格（3行）
  const { summaryFields } = React.useMemo(() => {
    const templateMeta = summaryTemplateMeta;

    const tm: Array<{ code: string; title?: string }> = (templateMeta ?? []) as Array<{ code: string; title?: string }>;
    const colsFromMeta = tm
      .filter((f) => !!f && typeof f.code === 'string' && f.code.length > 0)
      .map((f) => ({ key: f.code, label: f.title || f.code }));

    let columnsUsed = colsFromMeta;

    const summaryFieldKeys: string[] = Array.isArray(moduleItem?.summaryField)
      ? (moduleItem.summaryField as string[]).filter((fld) => typeof fld === 'string' && fld.length > 0)
      : [];
    if (summaryFieldKeys.length) {
      const keep = new Set(summaryFieldKeys);
      columnsUsed = columnsUsed.filter(c => keep.has(c.key));
    }

    const fieldsString = columnsUsed.map(c => c.key).join(',');

    return {
      summaryFields: fieldsString,
    };
  }, [moduleItem, summaryTemplateMeta]);

  const buildInputs = React.useCallback(() => {
    return {
      selected_report: selectedReport,
      search_condition: JSON.stringify(params || {}),
      search_fields: summaryFields || '',
      dhb_skey: sessionId,
    };
  }, [selectedReport, params, summaryFields, sessionId]);

  React.useEffect(() => {
    const run = async () => {
      if (abortRef.current) abortRef.current.abort();
      const abortController = new AbortController();
      abortRef.current = abortController;

      const inputs = buildInputs();
      setSummary('分析中...');
      setIsGenerating(true);

      try {
        const path = `/api/external/dify/${moduleItem?.difyAppId || ''}/workflows/run`;

        const resp = await postEventStream(
          path,
          {
            inputs,
            response_mode: 'streaming',
          },
          { signal: abortController.signal }
        );

        if (!resp.ok || !resp.body) {
          throw new Error('请求失败或不支持流式响应');
        }

        const reader = resp.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let accumulated = '';
        let finishedEarly = false; // 新增：收到 [DONE] 时主动结束

        while (true) {
          const { value, done } = await reader.read();
          if (done || finishedEarly) break;
          const chunk = decoder.decode(value, { stream: true });

          const lines = chunk.split('\n');
          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed.startsWith('data:')) continue;

            const payload = trimmed.replace(/^data:\s*/, '');
            if (payload === '[DONE]') {
              finishedEarly = true;
              try { await reader.cancel(); } catch {}
              break;
            }

            try {
              const obj = JSON.parse(payload);

              // dify 事件类型，只处理 workflow_finished 事件
              // 一些结果会在多个事件中返回导致重复
              if (obj.event !== 'workflow_finished') {
                continue;
              }

              // 大模型总结输出内容，包含在 aISummary 字段中
              // text 为大模型原始输出内容
              const textCandidate =
                obj?.aISummary ||
                obj?.aISummary ||
                obj?.data?.aISummary ||
                obj?.data?.outputs?.aISummary ||
                obj?.aISummary;

              if (textCandidate) {
                accumulated += textCandidate;
                setSummary(filterThinkContent(accumulated));
              }
            } catch {
              // 非JSON事件或心跳，忽略
            }
          }
        }

        setSummary(filterThinkContent(accumulated) || '暂无总结');
      } catch (e: any) {
        if (e?.name === 'AbortError') return;
        setSummary('AI总结生成失败或登录失效，请重试');
      } finally {
        setIsGenerating(false);
      }
    };

    const timer = setTimeout(() => {
      run();
    }, ANALYZE_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [buildInputs, refreshTick]);

  React.useEffect(() => {
    if (!isGenerating) return;
    const el = contentRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(id);
  }, [summary, isGenerating]);

  return (
    <Container $maxHeightPx={FIXED_MAX_HEIGHT_PX}>
      <div className="header">
        <span>{moduleItem.title || 'AI智能总结'}</span>
      </div>

      <div className="content" ref={contentRef}>
        <Markdown remarkPlugins={[remarkGfm]}>
          {summary || (loading ? '分析中...' : '暂无总结')}
        </Markdown>
      </div>

      <div className="bottom">
        <Button
          className="action-btn"
          type="default"
          size="small"
          shape="round"
          onClick={() => setDrawerVisible(true)}
          icon={<RightOutlined />}
          aria-label={'查看全部'}
          title={'查看全部'}
        >
          查看全部
        </Button>
        <Button
          className="action-btn"
          type="default"
          size="small"
          shape="round"
          onClick={handleRegenerate}
          disabled={isGenerating}
          icon={<SyncOutlined spin={isGenerating} />}
          aria-label={isGenerating ? '分析中' : '重新分析'}
          title={isGenerating ? '分析中' : '重新分析'}
        >
          {isGenerating ? '分析中' : '重新分析'}
        </Button>
        {!isAiChat && isMainFilter && showAiButton && (
          <Button
            type="primary"
            size="small"
            shape="round"
            onClick={() => onTriggerAiSearch && onTriggerAiSearch()}
            icon={<LineChartOutlined />}
          >
            AI数据洞察
          </Button>
        )}
      </div>
      <Drawer
        title={moduleItem.title || 'AI智能总结'}
        placement="right"
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        width={720}
      >
        <div className="content">
          <Markdown remarkPlugins={[remarkGfm]}>
            {summary || (loading ? '分析中...' : '暂无总结')}
          </Markdown>
        </div>
      </Drawer>
    </Container>
  );
};

const Container = styled.div<{ $maxHeightPx: number }>`
  background: #fff;
  padding: 12px;
  margin: 0 0 16px 0;
  width: 100%;

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
    margin-bottom: 8px;
    color: rgba(0, 0, 0, 0.45);
  }

  .content {
    max-height: ${p => p.$maxHeightPx}px;
    overflow-y: auto;
    width: 100%;
  }

  .bottom {
    display: flex;
    justify-content: center;
    margin-top: 8px;
  }

  .action-btn {
    margin-right: 8px;
  }
`;

export default AiSummary;
